import React, { FormEvent, useEffect, useState } from "react"
import { Search } from "lucide-react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import {
  STUDENT_QUESTIONS_PATH,
} from "./config/studentNavItems"
import { confirmIfUnsaved } from "./unsavedChangesGate"
import "./SearchField.style.scss"

interface SearchFieldProps {
  autoFocus?: boolean
  className?: string
  placeholder?: string
  onSubmitSearch?: () => void
}

const readQuery = (params: URLSearchParams) => (params.get("q") || "").trim()

export const learnerQuestionSearchPath = (keyword: string) => {
  const q = keyword.trim()
  if (!q) return STUDENT_QUESTIONS_PATH
  return `${STUDENT_QUESTIONS_PATH}?q=${encodeURIComponent(q)}`
}

const SearchField = ({
  autoFocus = false,
  className = "",
  placeholder = "Search by keyword, year, or topic...",
  onSubmitSearch,
}: SearchFieldProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [params, setParams] = useSearchParams()
  const urlQuery = readQuery(params)
  const [value, setValue] = useState(urlQuery)

  useEffect(() => {
    setValue(urlQuery)
  }, [urlQuery])

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    const keyword = value.trim()
    const onQuestions = location.pathname === STUDENT_QUESTIONS_PATH

    const go = () => {
      if (onQuestions) {
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev)
            if (keyword) next.set("q", keyword)
            else next.delete("q")
            next.delete("id")
            return next
          },
          { replace: true }
        )
      } else {
        navigate(learnerQuestionSearchPath(keyword))
      }
      onSubmitSearch?.()
    }

    confirmIfUnsaved(go)
  }

  return (
    <form
      className={`student-search-field ${className}`.trim()}
      role="search"
      onSubmit={handleSubmit}
    >
      <button
        type="submit"
        className="student-search-field__submit"
        aria-label="Search questions"
      >
        <Search className="student-search-field__icon" aria-hidden />
      </button>
      <input
        type="search"
        name="q"
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder}
        className="student-search-field__input"
        aria-label="Search questions"
        autoComplete="off"
        onChange={(event) => setValue(event.target.value)}
      />
    </form>
  )
}

export default SearchField
